import type { Tool } from "@webuild-ai/agent-platform/models/components";
import type { ToolRegistryEntry } from "@/utils/playground/toolDisplay";

import { useState, useEffect, useRef } from "react";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

let cachedTools: ToolRegistryEntry[] | null = null;

/**
 * The single empty result, shared so the failure path sets state to the value it
 * already holds and React bails out of the re-render. Returning a fresh `[]`
 * instead would change state identity on every failure, so a consumer that
 * re-renders for its own reasons would keep seeing a new array. The effect's
 * dependency on client presence rather than client identity, below, is the other
 * half of the same guarantee.
 */
const EMPTY_TOOLS: ToolRegistryEntry[] = [];

/**
 * The in-flight request, shared so a canvas that mounts several tool nodes at
 * once issues one request rather than one per node. `useAvailableModels`
 * caches only the resolved value, which suits its calmer render pattern, but a
 * workflow with five connectors renders five tool cards in the same commit and
 * every one of them would otherwise fetch the list. Resolves `null` on
 * failure, so subscribers can tell a failed read from a genuinely empty
 * registry; the failure itself is not cached, so a later mount retries.
 */
let inFlightRequest: Promise<ToolRegistryEntry[] | null> | null = null;

/**
 * Maps the SDK's `Tool` (id, name, description and icon, all required strings
 * in `@webuild-ai/agent-platform` 1.4.0) onto the registry entry shape. Only
 * `aliases` is read through an escape hatch, because the field is absent from
 * 1.4.0's zod schema (which strips undeclared keys) and so arrives, if at all,
 * only once a regenerated client ships. Everything else stays typed, so a
 * field rename in a future SDK fails to compile here rather than yielding
 * `id: undefined` at runtime.
 */
const toRegistryEntry = (tool: Tool): ToolRegistryEntry => {
  const withAliases = tool as Tool & { aliases?: unknown };

  return {
    id: tool.id,
    name: tool.name || tool.id,
    description: tool.description || "",
    icon: tool.icon || "",
    aliases: Array.isArray(withAliases.aliases)
      ? withAliases.aliases.filter((alias): alias is string => typeof alias === "string")
      : [],
  };
};

export interface AvailableToolsState {
  tools: ToolRegistryEntry[];
  /**
   * True only while a first read is outstanding. It goes false on both success
   * and failure, and is false from the start when the cache is already warm, so
   * a caller gating a panel on it can never be held on a spinner for ever.
   */
  isLoading: boolean;
  /**
   * True when the last read failed, so an empty `tools` can be told apart from
   * a tenant with no enabled connectors — the two used to render identically
   * as "no tools found", which blamed the tenant's configuration for a network
   * fault. Cleared when a retry (a later mount, since failures are not cached)
   * succeeds.
   */
  hasError: boolean;
}

/**
 * Shared hook for the platform's tool (connector) registry, served by
 * `GET /platform/v1/tools/` and filtered per tenant so disabled integrations
 * drop out.
 *
 * Non-throwing by design: a failed fetch logs, resolves to an empty list and
 * reports `hasError`, which every caller already tolerates by falling back to
 * the tool id (see `resolveToolDisplay`). A failure is not cached, so a later
 * mount retries.
 *
 * Most callers want `useAvailableTools` below and can ignore the loading and
 * error flags, which exist for the surfaces that gate their content on the
 * read.
 */
export const useAvailableToolsState = (): AvailableToolsState => {
  const { platformClient } = useWebuildAPIClient();
  const [tools, setTools] = useState<ToolRegistryEntry[]>(cachedTools || EMPTY_TOOLS);
  const [isLoading, setIsLoading] = useState(cachedTools === null);
  const [hasError, setHasError] = useState(false);

  /**
   * The client is read through a ref so the effect can depend on whether one
   * exists rather than on its identity, which makes the read happen once per
   * mount however the consumer's provider behaves. Sharing `EMPTY_TOOLS` above
   * keeps `tools` stable across a failure for the same reason, but on its own it
   * is not enough: the failure path also has to move `isLoading` from true to
   * false, and that re-render hands back a fresh client object from any provider
   * that does not memoise, which re-runs the effect and fires the request again.
   * With an endpoint that keeps failing, that is an unbounded refetch loop.
   * `WeBuildClientProvider` does memoise, so it was dormant there, and it showed
   * up as a flaky test rather than as a live fault.
   *
   * The trade-off is that a client swapped in without a remount, such as after a
   * token refresh, no longer retries by itself. That costs nothing when the
   * first read succeeded, because the result is cached module-level, and the
   * next mount retries when it did not.
   */
  const clientRef = useRef(platformClient);

  clientRef.current = platformClient;

  const hasClient = Boolean(platformClient);

  useEffect(() => {
    if (cachedTools) {
      setTools(cachedTools);
      setIsLoading(false);
      setHasError(false);

      return;
    }

    const client = clientRef.current;

    if (!client) {
      // No client yet, so there is nothing outstanding to wait for. Reporting
      // "loading" here would strand a gated panel on a spinner in the embedded
      // and unauthenticated cases, where the client may never arrive.
      setIsLoading(false);

      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setHasError(false);

    if (!inFlightRequest) {
      inFlightRequest = (async () => {
        try {
          const result = await client.tools.list();
          const mapped = result.map(toRegistryEntry);

          cachedTools = mapped;

          return mapped;
        } catch (error) {
          logger.error("Failed to fetch tools", error);

          return null;
        } finally {
          inFlightRequest = null;
        }
      })();
    }

    inFlightRequest.then((result) => {
      if (cancelled) return;

      setTools(result ?? EMPTY_TOOLS);
      setHasError(result === null);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [hasClient]);

  return { tools, isLoading, hasError };
};

/** The registry alone, for the callers that do not gate on the read. */
export const useAvailableTools = (): ToolRegistryEntry[] => useAvailableToolsState().tools;
